import { Helmet } from 'react-helmet-async';
import { getSiteUrl, SITE_NAME, SITE_SOCIAL_LINKS, toolRegistry, type ToolEntry } from '../../data/toolRegistry';
import type { BlogArticle } from '../../data/blogRegistry';

export interface FAQEntry {
  question: string;
  answer: string;
}

// ── SoftwareApplication Schema ──
export function SoftwareApplicationJsonLd({ tool }: { tool: ToolEntry }) {
  const siteUrl = getSiteUrl();
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: `${tool.h1} — ${SITE_NAME}`,
    description: tool.metaDescription,
    url: `${siteUrl}${tool.canonicalPath}`,
    applicationCategory: 'BusinessApplication',
    operatingSystem: 'Web Browser',
    offers: {
      '@type': 'Offer',
      price: tool.pricing === 'free' ? '0' : '0',
      priceCurrency: 'INR',
      availability: 'https://schema.org/InStock',
    },
    aggregateRating: undefined, // Add when you have real reviews
  };

  return (
    <Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
    </Helmet>
  );
}

// ── FAQPage Schema ──
export function FAQPageJsonLd({ tool }: { tool: ToolEntry }) {
  if (!tool.faqs.length) return null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: tool.faqs.map(faq => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
    </Helmet>
  );
}

// ── Generic FAQPage Schema (for website-level FAQ pages) ──
export function GenericFAQPageJsonLd({ faqs }: { faqs: FAQEntry[] }) {
  if (!faqs.length) return null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
    </Helmet>
  );
}

// ── HowTo Schema ──
export function HowToJsonLd({ tool }: { tool: ToolEntry }) {
  if (!tool.howToSteps.length) return null;

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'HowTo',
    name: `How to Use the ${tool.h1}`,
    description: tool.metaDescription,
    step: tool.howToSteps.map((step, index) => ({
      '@type': 'HowToStep',
      position: index + 1,
      name: step.name,
      text: step.text,
    })),
    tool: {
      '@type': 'HowToTool',
      name: SITE_NAME,
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
    </Helmet>
  );
}

// ── Article Schema (for blog posts) ──
export function ArticleJsonLd({ article }: { article: BlogArticle }) {
  const siteUrl = getSiteUrl();
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.metaDescription,
    url: `${siteUrl}${article.canonicalPath}`,
    datePublished: article.publishDate,
    dateModified: article.publishDate,
    author: {
      '@type': 'Organization',
      name: article.author,
    },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      logo: {
        '@type': 'ImageObject',
        url: `${siteUrl}/logo.png`,
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}${article.canonicalPath}`,
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
    </Helmet>
  );
}

// ── WebSite Schema (for homepage) ──
export function WebSiteJsonLd() {
  const siteUrl = getSiteUrl();
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: SITE_NAME,
    url: siteUrl,
    description: 'Solo OS is the all-in-one toolkit for freelancers. Generate proposals, contracts, invoices, NDAs, and legal notices — 100% in your browser.',
  };

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: SITE_NAME,
    url: siteUrl,
    sameAs: [SITE_SOCIAL_LINKS.instagram, SITE_SOCIAL_LINKS.x, SITE_SOCIAL_LINKS.linkedin, SITE_SOCIAL_LINKS.youtube],
    logo: {
      '@type': 'ImageObject',
      url: `${siteUrl}/logo.png`,
    },
    image: `${siteUrl}/logo.png`,
  };

  return (
    <Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(organizationSchema) }} />
    </Helmet>
  );
}

// ── Combined Tool Schema (convenience wrapper) ──
export function ToolPageJsonLd({ tool }: { tool: ToolEntry }) {
  return (
    <>
      <SoftwareApplicationJsonLd tool={tool} />
      <FAQPageJsonLd tool={tool} />
      <HowToJsonLd tool={tool} />
    </>
  );
}

// ── Schema-only tool wrapper ──
// For a tool page that already sets its own <SEOHead> (currently /sign) and so
// only needs the structured data the registry can supply.
export function ToolSchemaOnly({ slug }: { slug: string }) {
  const tool = toolRegistry.find((entry) => entry.slug === slug);
  if (!tool) return null;

  return (
    <>
      <ToolPageJsonLd tool={tool} />
      <BreadcrumbJsonLd
        items={[
          { name: 'Home', path: '/' },
          { name: tool.h1, path: tool.canonicalPath },
        ]}
      />
    </>
  );
}

// ── ItemList Schema (for index pages) ──
export function ItemListJsonLd({
  name,
  path,
  items,
}: {
  name: string;
  path: string;
  items: { name: string; description: string; path: string }[];
}) {
  const siteUrl = getSiteUrl();
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    '@id': `${siteUrl}${path}#itemlist`,
    name,
    numberOfItems: items.length,
    itemListOrder: 'https://schema.org/ItemListOrderAscending',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      description: item.description,
      url: `${siteUrl}${item.path}`,
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
    </Helmet>
  );
}

// ── Generic page schema ──
// For pages that are not tools or articles but still need an identity in the
// graph: contact and the legal pages.
export function PageJsonLd({
  type = 'WebPage',
  name,
  description,
  path,
}: {
  type?: 'WebPage' | 'ContactPage' | 'CollectionPage' | 'AboutPage';
  name: string;
  description: string;
  path: string;
}) {
  const siteUrl = getSiteUrl();
  const schema = {
    '@context': 'https://schema.org',
    '@type': type,
    '@id': `${siteUrl}${path}#webpage`,
    url: `${siteUrl}${path}`,
    name,
    description,
    inLanguage: 'en-IN',
    isPartOf: { '@type': 'WebSite', name: SITE_NAME, url: siteUrl },
    publisher: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: siteUrl,
      logo: { '@type': 'ImageObject', url: `${siteUrl}/logo.png` },
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
    </Helmet>
  );
}

// ── BreadcrumbList Schema ──
export function BreadcrumbJsonLd({ items }: { items: { name: string; path: string }[] }) {
  const siteUrl = getSiteUrl();
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.name,
      item: `${siteUrl}${item.path}`,
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />
    </Helmet>
  );
}
